import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTabletMode } from '../context/TabletModeContext';

/**
 * SubHeader — fixed secondary header that sits below the global TopCartBar.
 *
 * Used by CategoryView and ExperiencesShowcase. It centralises the "top offset"
 * math (safe-area + global TopCartBar height) and the back-button + title
 * styling, so we don't have to duplicate calc() expressions across views.
 *
 * Props:
 *   onBack    — () => void  (callback for the "Volver / Back" button)
 *   backLabel — string      (button label, already localized)
 *   title     — string|node (the heading next to the back button)
 *   testid    — optional data-testid for the header element
 */
export const SubHeader = ({ onBack, backLabel, title, testid = 'sub-header' }) => {
  const { isDark } = useTheme();
  const { isTablet } = useTabletMode();

  // Tablet keeps the TopCartBar visibly taller (~76-90px). We offset further so
  // the secondary header never overlaps with the iPad status bar + cart bar.
  const topOffset = isTablet ? '90px' : '60px';

  return (
    <header
      className="glass px-4 transition-colors duration-300"
      style={{
        position: 'fixed',
        top: `calc(env(safe-area-inset-top, 0px) + ${topOffset})`,
        left: 0, right: 0, zIndex: 40,
        paddingTop: isTablet ? '24px' : '20px',
        paddingBottom: isTablet ? '20px' : '16px',
        borderBottom: isDark
          ? '1px solid rgba(255,255,255,0.1)'
          : '1px solid rgba(183, 140, 60, 0.15)',
        backgroundColor: isDark ? 'rgba(26,26,26,0.97)' : 'rgba(251,245,232,0.97)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
      data-testid={testid}
    >
      <div className="max-w-5xl mx-auto flex items-center gap-3 sm:gap-5 min-w-0">
        <button
          onClick={onBack}
          className={`back-btn flex-shrink-0 ${isDark ? 'dark-mode' : ''}`}
          data-testid="sub-header-back-btn"
          style={isTablet ? { fontSize: '18px', padding: '12px 20px', gap: '10px' } : undefined}
        >
          <ArrowLeft size={isTablet ? 26 : 22} />
          {backLabel}
        </button>
        <h1
          className={`font-serif truncate transition-colors duration-300 ${isTablet ? 'text-3xl' : 'text-lg sm:text-xl'}`}
          style={{ color: isDark ? '#f5f5f5' : 'var(--stone-800)', minWidth: 0 }}
        >
          {title}
        </h1>
      </div>
    </header>
  );
};

/**
 * Returns the matching in-flow spacer height that clears SubHeader + TopCartBar.
 * Use it as: <div style={{ height: subHeaderSpacerHeight(isTablet) }} />
 */
export const subHeaderSpacerHeight = (isTablet) =>
  `calc(env(safe-area-inset-top, 0px) + ${isTablet ? '180px' : '120px'})`;

export default SubHeader;
